import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API, formatCurrency, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, AlertTriangle, Loader2, Search } from "lucide-react";
import axios from "axios";

const publicApi = axios.create({ baseURL: API });

// Accepts a full audit link (…/audit/<token>) or a raw token.
function extractToken(input) {
  const s = (input || "").trim();
  if (!s) return "";
  const m = s.match(/\/audit\/([^/?#]+)/);
  if (m) return m[1];
  try {
    const u = new URL(s);
    const parts = u.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  } catch {
    return s;
  }
}

export default function VerifyAudit() {
  const [params] = useSearchParams();
  const [input, setInput] = useState(params.get("token") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async (e) => {
    e?.preventDefault();
    const token = extractToken(input);
    if (!token) { setError("Paste an audit link or verification token to check."); return; }
    setError(""); setResult(null); setLoading(true);
    try {
      const { data } = await publicApi.get(`/public/verify/${encodeURIComponent(token)}`);
      setResult(data);
    } catch {
      setError("Could not reach the verification service. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <header className="border-b border-cream-200 bg-cream-50/90 backdrop-blur">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 h-16 flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-md bg-forest-700 grid place-items-center">
            <span className="font-heading text-cream-50 text-sm font-semibold">3</span>
          </div>
          <div className="leading-tight">
            <div className="font-heading text-[15px] font-semibold text-forest-800">Sign-Off</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">Audit Trail Verifier</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-10 sm:py-14">
        <div className="animate-fade-up">
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Independent verification</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-forest-900 tracking-tight">Verify an audit trail</h1>
          <p className="text-sm text-ink-soft mt-3 max-w-xl">
            Paste the audit link from a Sign-Off record (or its verification token) to confirm — without logging in —
            that the approval trail is authentic and has not been altered since it was created.
          </p>

          <form onSubmit={run} className="mt-6 flex flex-col sm:flex-row gap-2" data-testid="verify-form">
            <Input
              value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="https://sign-off.co.uk/audit/…  or  token"
              data-testid="verify-input"
              className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
            />
            <Button type="submit" disabled={loading} data-testid="verify-submit"
              className="bg-forest-700 hover:bg-forest-800 text-cream-50 shrink-0">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1.5" /> Verify</>}
            </Button>
          </form>

          {error && <div className="mt-4 text-sm text-rose-700" data-testid="verify-error">{error}</div>}

          {result && !result.found && (
            <div className="mt-6 rounded-xl border border-cream-200 bg-white px-5 py-6 text-center" data-testid="verify-notfound">
              <AlertTriangle className="h-6 w-6 text-amber-500 mx-auto mb-2" />
              <div className="font-heading text-lg text-forest-900">No record found</div>
              <p className="text-sm text-ink-soft mt-1">That link or token doesn&apos;t match any Sign-Off audit trail.</p>
            </div>
          )}

          {result && result.found && (
            <div
              className={`mt-6 rounded-xl border px-5 py-6 ${result.verified ? "border-forest-100 bg-forest-50/60" : "border-rose-200 bg-rose-50"}`}
              data-testid="verify-result"
            >
              <div className={`flex items-center gap-2.5 ${result.verified ? "text-forest-800" : "text-rose-700"}`}>
                {result.verified ? <ShieldCheck className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
                <div className="font-heading text-xl" data-testid="verify-status">
                  {result.verified ? "Authentic — not tampered" : "Warning — could not be verified"}
                </div>
              </div>
              <p className={`text-sm mt-2 ${result.verified ? "text-ink-soft" : "text-rose-700"}`}>
                {result.verified
                  ? "Every entry in this audit trail matches its cryptographic hash chain. Nothing has been edited, inserted, reordered or removed since it was recorded."
                  : "The hash chain for this record does not match. The trail may have been altered — do not rely on it without investigating."}
              </p>

              <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-sm">
                <Row k="Reference" v={<span className="font-mono">{result.reference}</span>} />
                <Row k="Payee" v={result.payee} />
                <Row k="Amount" v={formatCurrency(result.amount, result.currency)} />
                <Row k="Status" v={result.status?.replaceAll("_", " ")} />
                <Row k="Audit entries" v={result.entry_count} />
                <Row k="First action" v={formatDateTime(result.first_action_at)} />
                <Row k="Last action" v={formatDateTime(result.last_action_at)} />
                {result.verified && <Row k="Verification code" v={<span className="font-mono" data-testid="verify-code">{result.verification_code}</span>} />}
              </dl>
              <div className="text-[11px] text-ink-mute mt-4">Checked {formatDateTime(result.checked_at)}.</div>
            </div>
          )}
        </div>

        <p className="text-center text-[11px] text-ink-mute mt-10">
          Sign-Off · Tamper-proof, hash-chained audit trail
        </p>
      </main>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-cream-200/70 py-1.5">
      <dt className="text-ink-soft">{k}</dt>
      <dd className="text-forest-900 font-medium text-right">{v}</dd>
    </div>
  );
}
